<script setup lang="ts">
import { useExportsStore } from '@/presentation/stores/exports/exports.ts'
import { storeToRefs } from 'pinia'
import { computed, onMounted, type Ref, ref, watch } from 'vue'
import CBaseHeader from '@/presentation/components/base/CBaseHeader.vue'
import { default as PMessage } from 'primevue/message'
import { default as PProgressSpinner } from 'primevue/progressspinner'
import { useSchoolYearStore } from '@/presentation/stores/school/schoolYear.ts'
import { default as PSelect } from 'primevue/select'
import { default as PTag } from 'primevue/tag'
import type SchoolYear from '@/core/domain/school/year/SchoolYear.ts'

// Type
interface SchoolYearOptions {
  id: number
  name: string
  isCurrent: boolean
  isPast: boolean
  isFuture: boolean
}

// Composable
const exportsStore = useExportsStore()
const schoolYearStore = useSchoolYearStore()

// Store refs
const { exportSelectedSchoolYear, exportsStoreLoading, exportsStoreError, existsReportingState } =
  storeToRefs(exportsStore)
const { schoolYearStoreLoading, sortedSchoolYears } = storeToRefs(schoolYearStore)

// Refs
const componentLoading: Ref<boolean> = ref(true)
const localError: Ref<string | null> = ref(null)

// Computed
const loading = computed(() => {
  return (componentLoading.value || exportsStoreLoading.value.fetchAllExportFilters) ?? false
})

const hasError = computed(() => {
  return (localError.value !== null || exportsStoreError.value.hasError) ?? false
})

const errorMessage = computed(() => {
  return (localError.value || exportsStoreError.value.message) ?? ''
})

/**
 *
 * @param schoolYear
 */
const getYearStatus = (schoolYear: SchoolYearOptions | SchoolYear) => {
  if (schoolYear.isCurrent) return 'Active'
  return schoolYear.isFuture ? 'A venir' : 'Terminée'
}

/**
 * Charge les données des filtres d'export
 */
const loadData = async () => {
  try {
    componentLoading.value = true
    localError.value = null

    if (sortedSchoolYears.value.length === 0) {
      await schoolYearStore.fetchAllSchoolYears(true)
    }

    if (
      !exportsStoreLoading.value.fetchAllExportFilters &&
      exportSelectedSchoolYear.value !== null
    ) {
      await exportsStore.fetchAllExportFilters(exportSelectedSchoolYear.value.schoolYearId)
    }
  } catch (err) {
    localError.value =
      err instanceof Error ? err.message : 'Une erreur est survenue lors du chargement des données'
  } finally {
    componentLoading.value = false
  }
}

// LIFECYCLE
onMounted(async () => {
  try {
    exportsStore.setExportSelectedSchoolYear(
      sortedSchoolYears.value.find((option) => option.isCurrent) ?? null
    )
    await loadData()
  } catch (err) {
    console.error('Erreur lors du chargement initial:', err)
  }
})

watch(
  exportSelectedSchoolYear,
  async (newSchoolYear, oldSchoolYear) => {
    if (newSchoolYear && newSchoolYear.schoolYearId !== oldSchoolYear?.schoolYearId) {
      await loadData()
    }
  },
  { immediate: true }
)
</script>

<template>
  <c-base-header title="Export des données" :display-breadcrumbs="true" :has-widget="true">
    <template #widgets>
      <!-- Widget de sélection de période scolaire -->
      <template v-if="schoolYearStoreLoading.fetchAllSchoolYears || !exportSelectedSchoolYear">
        <div class="flex items-center gap-2">
          <p-progress-spinner style="width: 20px; height: 20px" strokeWidth="4" />
          <span>Chargement des périodes...</span>
        </div>
      </template>

      <template v-else>
        <h5 class="font-semibold">Période scolaire</h5>
        <p-select
          :options="sortedSchoolYears"
          v-model="exportSelectedSchoolYear"
          data-key="schoolYearId"
          :disabled="existsReportingState"
          v-if="sortedSchoolYears.length > 0"
          class="w-full min-w-min md:w-60"
          placeholder="Sélectionner une période"
        >
          <!--  Value template-->
          <template #value="slotProps: { value: SchoolYear; placeholder: string }">
            <div v-if="slotProps.value" class="flex items-center gap-2">
              <div>{{ slotProps.value.name }}</div>
              <p-tag
                class="self-start"
                :value="getYearStatus(slotProps.value)"
                :severity="slotProps.value.isCurrent ? 'success' : 'info'"
              />
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>

          <!-- Option template-->
          <template #option="slotProps: { option: SchoolYear }">
            <div class="flex items-center gap-2">
              <div>{{ slotProps.option.name }}</div>
              <p-tag
                class="self-start"
                :value="getYearStatus(slotProps.option)"
                :severity="slotProps.option.isCurrent ? 'success' : 'info'"
              />
            </div>
          </template>
        </p-select>
        <p-message
          v-if="sortedSchoolYears.length === 0"
          severity="info"
          class="mt-2 w-full"
          :closable="false"
        >
          Aucune année disponible.
        </p-message>
      </template>
    </template>
  </c-base-header>
  <div class="col-span-12 flex-1 gap-8 overflow-y-auto p-8 pt-2">
    <!-- Affichage des erreurs -->
    <p-message v-if="hasError" severity="error" :closable="false" class="mb-4">
      {{ errorMessage }}
    </p-message>

    <!-- État de chargement principal -->
    <div v-if="loading && !hasError" class="flex flex-col items-center justify-center p-8">
      <p-progress-spinner style="width: 50px; height: 50px" strokeWidth="5" />
      <p class="mt-4 text-lg">Chargement des données...</p>
    </div>
    <!-- Contenu de la page -->
    <router-view v-else />
  </div>
</template>